import type { BufferGeometry } from 'three';
import type { Vec2, Vec3, Ring, PolygonWithHoles } from '../../geo/types';
import type { VertexStyle } from './vertexStyle';
import { layoutToWorld } from '../../core/coords';
import type { DensifyConfig } from '../terrain/densify';
import { ANCHOR } from '../../render/anchorCode';
import { exaggerationCode, fixedExaggerationCode, grown } from './vertexChannels';
import { patternCode } from '../../render/patterns/patternIds';
import { cylinderSideCoords, planarFaceCoords, type FaceCoords } from './patternCoords';
import { newellNormal, quadNormal, transformNormal, transformPoint } from './frameMath';
import type { Frame, FaceMask } from './frameMath';
import { geometrySnapshot } from './geometrySnapshot';
import { groundTriangulation } from './groundTriangulation';
import { groundPlan } from './groundPlan';

export {
  transformDirection,
  transformNormal,
  transformPoint,
  type Frame,
  type FaceMask,
} from './frameMath';

export type { ExaggerationStyle, VertexStyle } from './vertexStyle';

export interface MeshBuilder {
  quad(
    style: VertexStyle,
    frame: Frame,
    corners: readonly [Vec3, Vec3, Vec3, Vec3],
    uv?: readonly [Vec2, Vec2, Vec2, Vec2]
  ): void;
  polygon(
    style: VertexStyle,
    frame: Frame,
    outline: readonly Vec3[],
    uvOf?: (local: Vec3) => Vec2
  ): void;
  box(style: VertexStyle, frame: Frame, min: Vec3, max: Vec3, faces?: FaceMask): void;
  prism(
    style: VertexStyle,
    frame: Frame,
    base: Ring,
    y0: number,
    y1: number,
    faces?: FaceMask
  ): void;
  cylinder(
    style: VertexStyle,
    frame: Frame,
    base: Vec3,
    radiusBottom: number,
    radiusTop: number,
    height: number,
    segments: number,
    faces?: FaceMask
  ): void;
  /** Coordinates are layout metres unless `uvOf` gives them at each layout point, the points the
   *  refinement adds included; either way the style's extent is kept. */
  groundPolygon(
    style: VertexStyle,
    polygon: PolygonWithHoles,
    y: number | ((layout: Vec2) => number),
    refinementConfig?: DensifyConfig,
    uvOf?: (layout: Vec2) => Vec2
  ): void;
  readonly vertexCount: number;
  readonly triangleCount: number;
  /** Final snapshot; releases staging capacity and forbids later writes. */
  finish(): BufferGeometry;
  build(): BufferGeometry;
}

const UNIT_SQUARE: readonly Vec2[] = [
  [0, 0],
  [1, 0],
  [1, 1],
  [0, 1],
];

/* A face without a uv of its own: metric coordinates when it is patterned, else the placeholder
   coordinates the painted shader's own surface patterns have always had there. */
function ownCoords(
  style: VertexStyle,
  frame: Frame,
  points: readonly Vec3[],
  normal: Vec3,
  plain: readonly Vec2[]
): FaceCoords {
  if (!style.pattern) return { uv: plain, extent: style.extent };
  return planarFaceCoords(points, normal, transformNormal([1, 0, 0], frame));
}

export function createMeshBuilder(initialVertexCapacity = 4096): MeshBuilder {
  let positions = new Float32Array(initialVertexCapacity * 3);
  let normals = new Float32Array(initialVertexCapacity * 3);
  let colors = new Float32Array(initialVertexCapacity * 3);
  let facade = new Float32Array(initialVertexCapacity * 4);
  let meta = new Float32Array(initialVertexCapacity * 4);
  // Most terrain and domestic fabric carries none of these channels. Allocate one only when a
  // vertex actually uses it; the zero-filled prefix still covers every earlier vertex.
  let exaggeration: Float32Array | undefined;
  let axisOffset: Float32Array | undefined;
  let painterOffset: Float32Array | undefined;
  let indices = new Uint32Array(initialVertexCapacity * 2);

  let vertexCount = 0;
  let triangleCount = 0;
  let finished = false;

  function ensureCapacity(needed: number): void {
    if (finished) throw new Error('Mesh builder is finalized');
    if (vertexCount + needed <= positions.length / 3) return;
    const vertices = Math.max((positions.length / 3) * 2, vertexCount + needed);
    positions = grown(positions, vertices, 3);
    normals = grown(normals, vertices, 3);
    colors = grown(colors, vertices, 3);
    facade = grown(facade, vertices, 4);
    meta = grown(meta, vertices, 4);
    if (exaggeration) exaggeration = grown(exaggeration, vertices, 2);
    if (axisOffset) axisOffset = grown(axisOffset, vertices, 3);
    if (painterOffset) painterOffset = grown(painterOffset, vertices, 3);
  }

  function addVertex(
    pos: Vec3,
    norm: Vec3,
    style: VertexStyle,
    uv: Vec2,
    extent: readonly [number, number],
    pattern: number,
    fixedAnchor: number | undefined
  ): number {
    const i = vertexCount;
    const i3 = i * 3;
    const i4 = i * 4;

    positions[i3] = pos[0];
    positions[i3 + 1] = pos[1];
    positions[i3 + 2] = pos[2];

    normals[i3] = norm[0];
    normals[i3 + 1] = norm[1];
    normals[i3 + 2] = norm[2];

    colors[i3] = style.color[0];
    colors[i3 + 1] = style.color[1];
    colors[i3 + 2] = style.color[2];

    facade[i4] = uv[0];
    facade[i4 + 1] = uv[1];
    facade[i4 + 2] = extent[0];
    facade[i4 + 3] = extent[1];

    meta[i4] = style.surface;
    meta[i4 + 1] = style.edgeId;
    meta[i4 + 2] = pattern;
    meta[i4 + 3] = style.strokeAngle;

    const exag = style.exaggeration;
    if (exag) exaggeration ??= new Float32Array((positions.length / 3) * 2);
    if (exaggeration) {
      exaggeration[i * 2] = exag ? (fixedAnchor ?? exaggerationCode(exag, pos)) : ANCHOR.none;
      exaggeration[i * 2 + 1] = exag?.base ?? 0.0;
    }

    const offset = style.axisOffset;
    if (offset && (offset[0] !== 0 || offset[1] !== 0 || offset[2] !== 0))
      axisOffset ??= new Float32Array(positions.length);
    if (axisOffset) {
      axisOffset[i3] = offset?.[0] ?? 0;
      axisOffset[i3 + 1] = offset?.[1] ?? 0;
      axisOffset[i3 + 2] = offset?.[2] ?? 0;
    }

    const moved = style.painterOffset?.(pos);
    if (moved && (moved[0] !== 0 || moved[1] !== 0 || moved[2] !== 0))
      painterOffset ??= new Float32Array(positions.length);
    if (painterOffset) {
      painterOffset[i3] = moved?.[0] ?? 0;
      painterOffset[i3 + 1] = moved?.[1] ?? 0;
      painterOffset[i3 + 2] = moved?.[2] ?? 0;
    }

    vertexCount++;
    return i;
  }

  function addTriangle(a: number, b: number, c: number): void {
    const index = triangleCount * 3;
    if (index + 3 > indices.length) {
      const next = new Uint32Array(Math.max(indices.length * 2, index + 3));
      next.set(indices);
      indices = next;
    }
    indices[index] = a;
    indices[index + 1] = b;
    indices[index + 2] = c;
    triangleCount++;
  }

  /* A convex face's vertices in order, fanned from the first. */
  function addFace(
    style: VertexStyle,
    points: readonly Vec3[],
    normal: Vec3,
    coords: FaceCoords
  ): void {
    ensureCapacity(points.length);
    const first = vertexCount;
    const pattern = style.pattern ? patternCode(style.pattern) : 0;
    const anchor = fixedExaggerationCode(style.exaggeration);
    for (let i = 0; i < points.length; i++)
      addVertex(points[i]!, normal, style, coords.uv[i]!, coords.extent, pattern, anchor);
    for (let i = 1; i < points.length - 1; i++) addTriangle(first, first + i, first + i + 1);
  }

  const builder: MeshBuilder = {
    quad(style, frame, corners, uv) {
      const points = corners.map((p) => transformPoint(p, frame));
      const normal = quadNormal(points[0]!, points[1]!, points[3]!);
      const coords = uv
        ? { uv, extent: style.extent }
        : ownCoords(style, frame, points, normal, UNIT_SQUARE);
      addFace(style, points, normal, coords);
    },

    polygon(style, frame, outline, uvOf) {
      if (outline.length < 3) return;
      const normal = transformNormal(newellNormal(outline), frame);
      const points = outline.map((p) => transformPoint(p, frame));
      const coords = uvOf
        ? { uv: outline.map((p) => uvOf(p)), extent: style.extent }
        : ownCoords(
            style,
            frame,
            points,
            normal,
            outline.map(() => UNIT_SQUARE[0]!)
          );
      addFace(style, points, normal, coords);
    },

    box(style, frame, min, max, faces = { top: true, bottom: true, sides: true }) {
      const corners: Vec3[] = [
        [min[0], min[1], min[2]],
        [max[0], min[1], min[2]],
        [max[0], min[1], max[2]],
        [min[0], min[1], max[2]],
        [min[0], max[1], min[2]],
        [max[0], max[1], min[2]],
        [max[0], max[1], max[2]],
        [min[0], max[1], max[2]],
      ];

      if (faces.bottom) {
        builder.quad(style, frame, [corners[0]!, corners[1]!, corners[2]!, corners[3]!]);
      }
      if (faces.top) {
        builder.quad(style, frame, [corners[7]!, corners[6]!, corners[5]!, corners[4]!]);
      }
      if (faces.sides) {
        builder.quad(style, frame, [corners[4]!, corners[5]!, corners[1]!, corners[0]!]);
        builder.quad(style, frame, [corners[5]!, corners[6]!, corners[2]!, corners[1]!]);
        builder.quad(style, frame, [corners[6]!, corners[7]!, corners[3]!, corners[2]!]);
        builder.quad(style, frame, [corners[7]!, corners[4]!, corners[0]!, corners[3]!]);
      }
    },

    prism(style, frame, base, y0, y1, faces = { top: true, bottom: true, sides: true }) {
      const n = base.length;
      if (n < 3) return;

      const bottomPts: Vec3[] = base.map((p) => [p[0], y0, -p[1]]);
      const topPts: Vec3[] = base.map((p) => [p[0], y1, -p[1]]);

      if (faces.bottom) {
        builder.polygon(style, frame, bottomPts.slice().reverse());
      }
      if (faces.top) {
        builder.polygon(style, frame, topPts);
      }
      if (faces.sides) {
        for (let i = 0; i < n; i++) {
          const j = (i + 1) % n;
          builder.quad(style, frame, [bottomPts[i]!, bottomPts[j]!, topPts[j]!, topPts[i]!]);
        }
      }
    },

    cylinder(style, frame, base, radiusBottom, radiusTop, height, segments, faces) {
      const mask = faces ?? { top: true, bottom: true, sides: true };
      const bottomPts: Vec3[] = [];
      const topPts: Vec3[] = [];

      for (let i = 0; i < segments; i++) {
        const angle = (i / segments) * Math.PI * 2;
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        bottomPts.push([base[0] + cos * radiusBottom, base[1], base[2] + sin * radiusBottom]);
        topPts.push([base[0] + cos * radiusTop, base[1] + height, base[2] + sin * radiusTop]);
      }

      // Ring points run from +X towards +Z, which is clockwise seen from above.
      if (mask.bottom && radiusBottom > 0) {
        builder.polygon(style, frame, bottomPts);
      }
      if (mask.top && radiusTop > 0) {
        builder.polygon(style, frame, topPts.slice().reverse());
      }
      for (let i = 0; mask.sides && i < segments; i++) {
        const j = (i + 1) % segments;
        const corners: [Vec3, Vec3, Vec3, Vec3] = [
          bottomPts[i]!,
          topPts[i]!,
          topPts[j]!,
          bottomPts[j]!,
        ];
        const side = style.pattern
          ? cylinderSideCoords(i, segments, radiusBottom, radiusTop, height)
          : undefined;
        builder.quad(side ? { ...style, extent: side.extent } : style, frame, corners, side?.uv);
      }
    },

    groundPolygon(style, polygon, y, refinementConfig, uvOf) {
      const { tiles, height, config } = groundPlan(polygon, y, refinementConfig);
      if (tiles) {
        for (const tile of tiles) builder.groundPolygon(style, tile, y, config, uvOf);
        return;
      }
      const tri = groundTriangulation(polygon, config);
      if (typeof height === 'function') {
        ensureCapacity(tri.indices.length);
        const pattern = style.pattern ? patternCode(style.pattern) : 0;
        const anchor = fixedExaggerationCode(style.exaggeration);
        const points: Vec3[] = [];
        const coords: Vec2[] = [];
        for (let i = 0; i < tri.positions.length; i += 2) {
          const p: Vec2 = [tri.positions[i]!, tri.positions[i + 1]!];
          points.push(layoutToWorld([Math.fround(p[0]), Math.fround(p[1])], height(p)));
          coords.push(uvOf ? uvOf(p) : p);
        }
        const corners: [Vec3, Vec3, Vec3] = [
          [0, 0, 0],
          [0, 0, 0],
          [0, 0, 0],
        ];
        for (let i = 0; i < tri.indices.length; i += 3) {
          const a = tri.indices[i]!;
          let b = tri.indices[i + 1]!,
            c = tri.indices[i + 2]!;
          corners[0] = points[a]!;
          corners[1] = points[b]!;
          corners[2] = points[c]!;
          const projected =
            (corners[1][2] - corners[0][2]) * (corners[2][0] - corners[0][0]) -
            (corners[1][0] - corners[0][0]) * (corners[2][2] - corners[0][2]);
          // Submillimetre clipped strips can collapse or reverse at GPU precision. A collapsed
          // footprint has no visible surface; otherwise its front must agree with stored winding.
          if (projected === 0) continue;
          if (projected < 0) {
            [corners[1], corners[2]] = [corners[2], corners[1]];
            [b, c] = [c, b];
          }
          const normal = newellNormal(corners);
          const first = vertexCount;
          addVertex(corners[0], normal, style, coords[a]!, style.extent, pattern, anchor);
          addVertex(corners[1], normal, style, coords[b]!, style.extent, pattern, anchor);
          addVertex(corners[2], normal, style, coords[c]!, style.extent, pattern, anchor);
          addTriangle(first, first + 1, first + 2);
        }
        return;
      }
      const baseIdx = vertexCount;
      const normal: Vec3 = [0, 1, 0];
      ensureCapacity(tri.positions.length / 2);
      const pattern = tri.positions.length && style.pattern ? patternCode(style.pattern) : 0;
      const anchor = tri.positions.length ? fixedExaggerationCode(style.exaggeration) : undefined;
      for (let i = 0; i < tri.positions.length; i += 2) {
        const p: Vec2 = [tri.positions[i]!, tri.positions[i + 1]!];
        addVertex(
          layoutToWorld(p, height),
          normal,
          style,
          uvOf ? uvOf(p) : p,
          style.extent,
          pattern,
          anchor
        );
      }
      for (let i = 0; i < tri.indices.length; i += 3) {
        addTriangle(
          baseIdx + tri.indices[i]!,
          baseIdx + tri.indices[i + 1]!,
          baseIdx + tri.indices[i + 2]!
        );
      }
    },

    get vertexCount(): number {
      return vertexCount;
    },

    get triangleCount(): number {
      return triangleCount;
    },

    build(): BufferGeometry {
      if (finished) throw new Error('Mesh builder is finalized');
      return geometrySnapshot(
        {
          positions,
          normals,
          colors,
          facade,
          meta,
          exaggeration,
          axisOffset,
          painterOffset,
          indices,
        },
        vertexCount,
        triangleCount
      );
    },

    finish(): BufferGeometry {
      const geometry = builder.build();
      finished = true;
      positions = normals = colors = facade = meta = new Float32Array(0);
      exaggeration = axisOffset = painterOffset = undefined;
      indices = new Uint32Array(0);
      return geometry;
    },
  };

  return builder;
}
